import {
  CompanyEquityPlanEnumType,
  ContractBilling,
  EmploymentsHiringCompanyProcessStepName,
  EmploymentsHiringEmployeeProcessStepName,
  EngagementState,
  MonthsEnum,
  ReimbursementBudgetLabelEnum,
  ResetFrequencyEnum,
  RightToWorkTypeEnum,
  VariableCompensationLabelEnum,
  VisaAcquisitionPayerTypeEnum,
} from "@oysterhr/schema"

export * from "./rubyConstants"

export const MiddeskReportURL = "https://app.middesk.com/businesses"

// TODO: Remove object.keys call on this object to convert it to an enum
export const EmploymentPaymentCadences = {
  unknown: "Unknown / unspecified",
  bimonthly: "Bimonthly", // Every 2 months
  monthly: "Monthly", // Every month
  semimonthly: "Semimonthly", // Twice a month
  fortnightly: "Fortnightly", // Every 2 weeks
  biweekly: "Biweekly", // Every 2 weeks
  weekly: "Weekly", // Every week
}

export const EmploymentVisaRightToWorkType = {
  [RightToWorkTypeEnum.Null]: "Null",
  [RightToWorkTypeEnum.Definite]: "Definite",
  [RightToWorkTypeEnum.Indefinite]: "Indefinite",
  [RightToWorkTypeEnum.BlueCard]: "Blue Card",
  [RightToWorkTypeEnum.FamilyVisa]: "Family Visa",
  [RightToWorkTypeEnum.LongTermResidencePermit]: "Long Term Residence Permit",
  [RightToWorkTypeEnum.PermanentResidencePermit]: "Permanent Residence Permit",
  [RightToWorkTypeEnum.TemporaryResidencePermit]: "Temporary Residence Permit",
  [RightToWorkTypeEnum.WorkPermit]: "Work Permit",
}

// TODO: Remove object.keys call on this object to convert it to an enum
export const EmploymentVisaAcquisitionPayer = {
  [VisaAcquisitionPayerTypeEnum.Unknown]: "Unknown",
  [VisaAcquisitionPayerTypeEnum.Customer]: "Customer",
  [VisaAcquisitionPayerTypeEnum.Oyster]: "Oyster",
  [VisaAcquisitionPayerTypeEnum.TeamMember]: "Team Member",
}

export const EmploymentAnnualBonusTypes = {
  unknown: "Unknown / unspecified",
  none: "None",
  vacation: "Vacation",
  "13th-month": "13th Month",
  "13th-and-14th-month": "13th and 14th Month",
}

// TODO: Remove object.keys call on this object to convert it to an enum (if we want to do it)
// export const EngagementState = {
//   DRAFT: "DRAFT",
//   ASSISTED_SETUP: "ASSISTED_SETUP",
//   IN_PROGRESS: "IN_PROGRESS",
//   WAITING_FOR_SIGNATURE: "WAITING_FOR_SIGNATURE",
//   ENGAGED: "ENGAGED",
//   TERMINATED: "TERMINATED",
// }

export const EngagementStateList = [
  EngagementState.Draft,
  EngagementState.AssistedSetup,
  EngagementState.InProgress,
  EngagementState.WaitingForSignature,
  EngagementState.Engaged,
  EngagementState.Terminated,
  EngagementState.Canceled,
  EngagementState.OnHold,
]

export const MonetizationPlanNames = ["classic", "scale", "employee", "contractor"]

export enum EmploymentStatus {
  DRAFT = "draft",
  WAITING_FOR_SIGNATURE = "waiting_for_signature",
  IN_PROGRESS = "assisted_setup",
  CANCELED = "canceled",
  ENGAGED = "engaged",
  TERMINATED = "terminated",
  ON_HOLD = "on_hold",
  DELETED = "deleted",
}

export enum ContractState {
  DRAFT = "draft",
  IN_PROGRESS = "in_progress",
  WAITING_FOR_SIGNATURE = "waiting_for_signature",
  ENGAGED = "engaged",
  TERMINATED = "terminated",
}

export const DEFAULT_DAYS_OF_NOTICE = 14
export const DEFAULT_DAYS_TO_CURE = 5
export const WORKING_DAYS_PER_YEAR = 260
export const DEFAULT_FULL_TIME_HOURS_PER_WEEK = 40
export const WEEKS_PER_YEAR = 52
export const WORKING_DAYS_PER_WEEK = WORKING_DAYS_PER_YEAR / WEEKS_PER_YEAR

export enum EmploymentEvent {
  SUBMIT = "submit",
  ENGAGE = "engage",
  TERMINATE = "terminate",
  REENGAGE = "reengage",
  DISENGAGE = "disengage",
  DRAFT = "draft",
  CANCEL = "cancel",
  DELETE = "archive",
  HOLD = "hold",
}

export const humanizedEmploymentState = (state: EmploymentStatus | ContractState | string) => {
  switch (state) {
    case EmploymentStatus.DRAFT:
    case EngagementState.Draft:
      return "Draft"
    case EmploymentStatus.IN_PROGRESS:
    case EngagementState.InProgress:
    case EngagementState.AssistedSetup:
    case ContractState.IN_PROGRESS:
      return "In progress"
    case EngagementState.WaitingForSignature:
    case ContractState.WAITING_FOR_SIGNATURE:
      return "Signing"
    case EngagementState.Engaged:
    case EmploymentStatus.ENGAGED:
      return "Engaged"
    case EngagementState.Terminated:
    case EmploymentStatus.TERMINATED:
      return "Terminated"
    case EngagementState.Canceled:
    case EmploymentStatus.CANCELED:
      return "Canceled"
    case EngagementState.OnHold:
    case EmploymentStatus.ON_HOLD:
      return "In progress"
    case "PENDING":
      return "Pending"
    // TODO: replace with ats candidate state
    default:
      return "Not a valid state"
  }
}

export const ContractBillingTypeList = [ContractBilling.Fixed, ContractBilling.PayAsYouGo]

export const usaStates = [
  { code: "AK", name: "Alaska" },
  { code: "AL", name: "Alabama" },
  { code: "AR", name: "Arkansas" },
  { code: "AS", name: "American Samoa" },
  { code: "AZ", name: "Arizona" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DC", name: "District of Columbia" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "GU", name: "Guam" },
  { code: "HI", name: "Hawaii" },
  { code: "IA", name: "Iowa" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "MA", name: "Massachusetts" },
  { code: "MD", name: "Maryland" },
  { code: "ME", name: "Maine" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MO", name: "Missouri" },
  { code: "MS", name: "Mississippi" },
  { code: "MT", name: "Montana" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "NE", name: "Nebraska" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NV", name: "Nevada" },
  { code: "NY", name: "New York" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VA", name: "Virginia" },
  { code: "VI", name: "Virgin Islands" },
  { code: "VT", name: "Vermont" },
  { code: "WA", name: "Washington" },
  { code: "WI", name: "Wisconsin" },
  { code: "WV", name: "West Virginia" },
  { code: "WY", name: "Wyoming" },
]

export const companyKindOptions = [
  "DEFAULT_NON_PROFIT",
  "DEFAULT_PARTNERSHIP",
  "DEFAULT_PRIVATE_COMPANY",
  "DEFAULT_PUBLIC_COMPANY",
  "DEFAULT_SOLE_TRADER",
  "UK_PARTNERSHIP",
  "UK_PRIVATE_LIMITED_COMPANY",
  "UK_PUBLIC_LIMITED_COMPANY",
  "UK_SOLE_PROPIETORSHIP",
  "UK_UNLIMITED_COMPANY",
  "US_CORPORATION",
  "US_LLC",
  "US_PARTNERSHIP",
  "US_SOLE_PROPIETORSHIP",
  "OTHER",
]

export enum PaymentMethodEnumType {
  CUSTOMER_BANK_ACCOUNT_SEPA = "PaymentMethodsCustomerBankAccountSepaType",
  CUSTOMER_BANK_ACCOUNT_ACH = "PaymentMethodsCustomerBankAccountAchType",
  USER_BANK_ACCOUNT = "EngagementBankAccount",
  STRIPE_ACH = "PaymentMethodStripeAch",
  STRIPE_SEPA = "PaymentMethodStripeSepa",
  STRIPE_BACS = "PaymentMethodStripeBacs",
  STRIPE_PAD = "PaymentMethodStripePad",
}

export enum TaxComplianceState {
  PROVIDED = "PROVIDED",
  EXPECTED = "EXPECTED",
}

export const TaxComplianceKind = {
  US_W9: "US W9",
  US_W8BEN: "US W8BEN",
  US_W8BENE: "US W8BENE",
  US_1099NEC: "US 1099-NEC",
} as const

export const EngagementPaymentDays: Record<string, string> = {
  unknown: "Unknown / unspecified",
  WORKED_MONTH_LAST_DAY: "Last day of worked month",
  ...[...Array(30).keys()].reduce<Record<string, string>>((result, key) => {
    result[`SUCCEEDING_MONTH_DAY_${key + 1}`] = `Day ${key + 1} of succeeding month`
    return result
  }, {}),
}

export const OysterFeeDiscountMonths = [
  { name: "Not Applicable", value: 0 },
  { name: "1 month", value: 1 },
  { name: "3 months", value: 3 },
  { name: "6 months", value: 6 },
  { name: "1 year", value: 12 },
  { name: "2 years", value: 24 },
  { name: "3 years", value: 36 },
  { name: "Indefinite", value: 1200 },
]

interface OysterFeeDiscountTimeLimit {
  name: string
  value: number
}

export const IndefiniteDiscountTimeLimitOption: OysterFeeDiscountTimeLimit = {
  name: "indefinitely",
  value: 1200,
}

export const OysterFeeDiscountTimeLimits: Array<OysterFeeDiscountTimeLimit> = [
  { name: "1 month", value: 1 },
  { name: "2 months", value: 2 },
  { name: "3 months", value: 3 },
  { name: "6 months", value: 6 },
  { name: "12 months", value: 12 },
  IndefiniteDiscountTimeLimitOption,
]

export const AddressType = {
  LIVING: "living_address",
  MAILING: "mailing_address",
  CORPORATE: "corporate_address",
  CORPORATE_BILLING: "corporate_billing_address",
  BILLING: "billing_address",
}

export const PaySupplementState = {
  PENDING: "PENDING",
  PAID: "PAID",
}

export enum FilterType {
  CHECKBOX = "CHECKBOX",
  CHECKBOX_GROUP = "CHECKBOX_GROUP",
  COMPANY = "COMPANY",
  COUNTRY = "COUNTRY",
  DATE = "DATE",
  ENGAGEMENT = "ENGAGEMENT", // this works with the Engagement model
  ENTITY = "ENTITY",
  INVOICE_SELECT = "INVOICE_SELECT",
  MULTISELECT = "MULTISELECT",
  NATIONALITY = "NATIONALITY",
  NESTED_CHECKBOX_GROUP = "NESTED_CHECKBOX_GROUP",
  PAYMENT_PERIOD = "PAYMENT_PERIOD",
  SELECT = "SELECT",
  STAFF_SELECT = "STAFF_SELECT",
  TEAM_MEMBER = "TEAM_MEMBER",
  VENDOR = "VENDOR",
  SWITCH = "SWITCH",
}

export const NewHireStatus = {
  NEW_HIRE: "NEW_HIRE",
  EXISTING_EMPLOYEE: "EXISTING_EMPLOYEE",
  EXISTING_CONTRACTOR: "EXISTING_CONTRACTOR",
}

export enum EngagementProbationPeriodEnum {
  DAYS = "DAYS",
  WEEKS = "WEEKS",
  MONTHS = "MONTHS",
}

export const EngagementProbationPeriod = {
  DAYS: EngagementProbationPeriodEnum.DAYS,
  WEEKS: EngagementProbationPeriodEnum.WEEKS,
  MONTHS: EngagementProbationPeriodEnum.MONTHS,
}

export const BenefitFrequency = {
  ONE_TIME: "ONE_TIME",
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  BIANNUAL: "BIANNUAL",
  YEARLY: "YEARLY",
}

export const OrderedBenefitFrequency = [
  BenefitFrequency.ONE_TIME,
  BenefitFrequency.MONTHLY,
  BenefitFrequency.QUARTERLY,
  BenefitFrequency.BIANNUAL,
  BenefitFrequency.YEARLY,
]

export const EquityPlansList = [CompanyEquityPlanEnumType.Base, CompanyEquityPlanEnumType.Premium]

export const allowedBonusTypes = [
  VariableCompensationLabelEnum.SignOnBonus,
  VariableCompensationLabelEnum.EndOfYear,
  VariableCompensationLabelEnum.VacationBonus,
  VariableCompensationLabelEnum.HolidayBonus,
  VariableCompensationLabelEnum.Performance,
  VariableCompensationLabelEnum.Retention,
  VariableCompensationLabelEnum.CommissionPercentage,
  VariableCompensationLabelEnum.CommissionAmount,
]

export const allowedAllowanceTypes = [
  VariableCompensationLabelEnum.HealthAndWellbeing,
  VariableCompensationLabelEnum.LearningDevelopment,
  VariableCompensationLabelEnum.Sustenance,
  VariableCompensationLabelEnum.Travel,
]

export const allowedReimbursementTypes = [
  ReimbursementBudgetLabelEnum.Benefits,
  ReimbursementBudgetLabelEnum.Car,
  ReimbursementBudgetLabelEnum.Equipment,
  ReimbursementBudgetLabelEnum.HomeOffice,
  ReimbursementBudgetLabelEnum.LearningAndDevelopment,
  ReimbursementBudgetLabelEnum.Food,
  ReimbursementBudgetLabelEnum.OfficeSupplies,
  ReimbursementBudgetLabelEnum.Rent,
  ReimbursementBudgetLabelEnum.Travel,
  ReimbursementBudgetLabelEnum.Other,
]

export const reimbursementFrequency = [
  ResetFrequencyEnum.Monthly,
  ResetFrequencyEnum.Quarterly,
  ResetFrequencyEnum.Biannual,
  ResetFrequencyEnum.Yearly,
  ResetFrequencyEnum.Never,
]

export const companyTierOptions = [
  { label: "No tier set", value: "NONE" },
  { label: "Tier 1", value: "TIER_1" },
  { label: "Tier 2", value: "TIER_2" },
  { label: "Tier 3", value: "TIER_3" },
]

export enum EarlyExerciseEvent {
  ACCELERATED = "ACCELERATED",
  TM_PERFORMANCE = "TM_PERFORMANCE",
  COMPANY_PERFORMANCE = "COMPANY_PERFORMANCE",
  ACQUISITION = "ACQUISITION",
  OTHER = "OTHER",
}

export enum EquityManagementSoftwareTool {
  CARTA = "CARTA",
  CAKE_EQUITY = "CAKE_EQUITY",
  SHAREWORKS = "SHAREWORKS",
  GLOBAL_SHARES = "GLOBAL_SHARES",
  CAPDESK = "CAPDESK",
  OTHER = "OTHER",
  NONE = "NONE",
}

export enum EquityDocumentType {
  PLAN = "equityPlan",
  REGISTRATION = "equityPlanRegistration",
  VALUATION = "equityCompanyValuation",
}

export const EquityIssueRiskTiers = [1, 2, 3]

export enum EquityIssuePlans {
  SOP = "sop",
  RSU = "rsu",
  CSH = "csh",
  KEY_ISSUES = "KeyIssues",
}

export const EquityIssuePlanList = [
  EquityIssuePlans.SOP,
  EquityIssuePlans.RSU,
  EquityIssuePlans.CSH,
  EquityIssuePlans.KEY_ISSUES,
]

export enum HiringBlockStepType {
  ACTIVE = "ACTIVE",
  PASSIVE = "PASSIVE",
}

export enum DocumentType {
  DOCUMENT = "Document",
  EXTERNAL_CONTRACT_REFERENCE = "ExternalContractReference",
}

export const EmployerOfRecordCutOffMonthDays = [
  { label: "2nd of the month", value: 2 },
  { label: "3rd of the month", value: 3 },
  { label: "4th of the month", value: 4 },
  { label: "5th of the month", value: 5 },
  { label: "6th of the month", value: 6 },
  { label: "7th of the month", value: 7 },
  { label: "8th of the month", value: 8 },
  { label: "9th of the month", value: 9 },
  { label: "10th of the month", value: 10 },
]

export const NewEmployerOfRecordCutOffMonthDays = [
  { label: "2nd of the month", value: 2 },
  { label: "3rd of the month", value: 3 },
  { label: "4th of the month", value: 4 },
  { label: "5th of the month", value: 5 },
  { label: "6th of the month", value: 6 },
  { label: "7th of the month", value: 7 },
  { label: "8th of the month", value: 8 },
  { label: "9th of the month", value: 9 },
  { label: "10th of the month", value: 10 },
  { label: "11th of the month", value: 11 },
  { label: "12th of the month", value: 12 },
  { label: "13th of the month", value: 13 },
  { label: "14th of the month", value: 14 },
  { label: "15th of the month", value: 15 },
  { label: "16th of the month", value: 16 },
  { label: "17th of the month", value: 17 },
  { label: "18th of the month", value: 18 },
  { label: "19th of the month", value: 19 },
  { label: "20th of the month", value: 20 },
]

export enum ModalVariant {
  Primary = "primary",
  Danger = "danger",
  DangerOutline = "danger--outline",
}

export const MAX_MONEY = 9007199254740990

export const SyncedFields = {
  PHONE_NUMBER: "phone_number",
  DATE_OF_BIRTH: "date_of_birth",
  MANAGER_ROLE: "manager_role",
  MANAGER_NAME: "manager_name",
  LIVING_ADDRESS: "living_address",
  MAILING_ADDRESS: "mailing_address",
  DEPARTMENT: "name",
  PERSONAL_EMAIL: "email",
  WORK_EMAIL: "work_email_address",
}

export const PushedFields = {
  PHONE_NUMBER: "phone_number",
  PHONE_COUNTRY_CODE: "phone_country_code",
  PERSONAL_EMAIL: "email",
  ROLE: "role",
  START_DATE: "start_date",
  ADDRESS_LINE_1: "address_line1",
  CITY: "city",
  POSTAL_CODE: "postal_code",
  COUNTRY_CODE: "country_code",
  NAME: "name",
}

export const ORDERED_MONTHS = [
  MonthsEnum.January,
  MonthsEnum.February,
  MonthsEnum.March,
  MonthsEnum.April,
  MonthsEnum.May,
  MonthsEnum.June,
  MonthsEnum.July,
  MonthsEnum.August,
  MonthsEnum.September,
  MonthsEnum.October,
  MonthsEnum.November,
  MonthsEnum.December,
] as MonthsEnum[]

const COMPANY_DISABLE_CHANGES_STEPS = [
  EmploymentsHiringCompanyProcessStepName.WaitingForSignatures,
  EmploymentsHiringCompanyProcessStepName.Completed,
]

const EMPLOYEE_DISABLE_CHANGES_STEPS = [
  EmploymentsHiringEmployeeProcessStepName.WaitingForSignatures,
  EmploymentsHiringEmployeeProcessStepName.Completed,
]

export const disableAgreementFieldsSteps = [
  ...COMPANY_DISABLE_CHANGES_STEPS,
  ...EMPLOYEE_DISABLE_CHANGES_STEPS,
]

export const EquityDocumentsDescriptions = {
  COMPANY_VALUATION_DOCUMENT: "Company valuation document",
  EQUITY_PLAN_REGISTERATION_DOCUMENT: "Equity plan registeration document",
  EQUITY_PLAN_DOCUMENT: "Equity plan document",
}

export const IntegrationStatusOptions = ["all", "online", "offline", "minor_issues"]

export const PushedAddressTypes = {
  LIVING: "living_address",
  CORPORATE: "corporate_address",
  CORPORATE_BILLING: "corporate_billing_address",
  BILLING: "billing_address",
  INDIVIDUAL_CONTRACTOR: "individual_contractor",
  COMPANY_CONTRACTOR: "company_contractor",
}

export const ApiSupportEmail = "api-support@oysterhr.com"
